import { useState, useCallback, useEffect, useRef } from 'react'

/**
 * 自動保存機能管理フック
 *
 * 責務:
 * - 自動保存ON/OFF設定の管理（localStorageと同期）
 * - 自動保存タイマーの管理（3秒後に保存）
 * - blur時の自動保存タイマー（500ms後に保存）
 * - タイマークリーンアップ処理
 */
export function useAutoSave(onSave: () => void) {
  const [autoSave, setAutoSave] = useState(() => {
    // localStorageから自動保存設定を読み込み（デフォルトはfalse）
    const saved = localStorage.getItem('prsheet_autosave')
    return saved === 'true'
  })

  const autoSaveTimerRef = useRef<number | null>(null)

  // 自動保存タイマーをクリア
  const clearAutoSaveTimer = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
      autoSaveTimerRef.current = null
    }
  }, [])

  // 保存をキューに追加（指定されたタイムアウト後に実行）
  const enqueueSave = useCallback(
    (timeout: number) => {
      clearAutoSaveTimer()
      autoSaveTimerRef.current = window.setTimeout(() => {
        onSave()
      }, timeout)
    },
    [clearAutoSaveTimer, onSave],
  )

  // 自動保存タイマーを設定（3秒後に保存）
  const scheduleAutoSave = useCallback(() => {
    if (!autoSave) return

    enqueueSave(3000)
  }, [autoSave, enqueueSave])

  // blur時の自動保存（500ms後に保存、連続blur時は前のキューをキャンセル）
  const handleBlurAutoSave = useCallback(() => {
    if (!autoSave) return

    enqueueSave(500)
  }, [autoSave, enqueueSave])

  // autoSave変更時にlocalStorageに保存
  useEffect(() => {
    localStorage.setItem('prsheet_autosave', autoSave.toString())
  }, [autoSave])

  // コンポーネントのアンマウント時にタイマーをクリア
  useEffect(() => {
    return () => {
      clearAutoSaveTimer()
    }
  }, [clearAutoSaveTimer])

  return {
    autoSave,
    setAutoSave,
    scheduleAutoSave,
    handleBlurAutoSave,
  }
}
